import React, { useEffect, useMemo, useState } from 'react';
import { useApp } from '@/contexts/AppContext';
import { useStrings } from '@/hooks/useStrings';
import { supabase } from '@/lib/supabase';
import { endOfCycleSummary } from '@/logic/cycle';

// EndOfCycleLetter, the moment of recognition that closes the loop
// on a cycle. Renders a one-time card on /home when the user has
// just started a new period AND her previous cycle had logged
// floor_feel data to summarize. Dismissing it (or simply seeing it
// once) writes a sentinel to localStorage so the card never reappears
// for the same cycle, even if the user navigates away and back.
//
// Without this card, every datum the user logged sat passive. With
// it, every cycle ends with her body being told back to her.

const STORAGE_KEY = 'bb_end_of_cycle_seen';

function getSeenSet() {
  if (typeof window === 'undefined') return new Set();
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return new Set(Array.isArray(arr) ? arr : []);
  } catch {
    return new Set();
  }
}

function markSeen(cycleStart) {
  if (typeof window === 'undefined') return;
  try {
    const seen = getSeenSet();
    seen.add(cycleStart);
    // Cap at 12 entries so the list never balloons.
    const trimmed = [...seen].slice(-12);
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed));
  } catch {}
}

const styles = {
  card: {
    background: 'var(--surface)',
    borderRadius: 18,
    padding: '18px 20px',
    margin: '0 0 16px',
    border: '1px solid color-mix(in srgb, var(--push, #E5736B) 22%, transparent)',
    boxShadow: '0 8px 22px -16px rgba(168,0,63,0.18)',
    position: 'relative',
  },
  eyebrow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontFamily: 'var(--font-ui)',
    fontSize: 11, fontWeight: 700,
    letterSpacing: '0.22em',
    textTransform: 'uppercase',
    color: 'var(--push, #E5736B)',
    marginBottom: 8,
  },
  eyebrowDot: {
    width: 6, height: 6, borderRadius: 999,
    background: 'var(--push, #E5736B)',
  },
  title: {
    fontFamily: 'var(--font-title)',
    fontStyle: 'italic',
    fontSize: 22, fontWeight: 500,
    color: 'var(--dark-brown)',
    margin: '0 0 10px',
    lineHeight: 1.2,
    letterSpacing: '-0.01em',
    fontVariationSettings: '"opsz" 72',
  },
  body: {
    fontFamily: 'var(--font-body)',
    fontSize: 15,
    lineHeight: 1.55,
    color: 'var(--dark-brown)',
    margin: '0 0 14px',
  },
  bodyMuted: {
    fontFamily: 'var(--font-body)',
    fontSize: 13,
    lineHeight: 1.45,
    color: 'var(--warm-brown)',
    margin: '0 0 14px',
    fontStyle: 'italic',
  },
  closeBtn: {
    position: 'absolute',
    top: 8,
    right: 8,
    width: 36, height: 36,
    border: 'none',
    background: 'transparent',
    color: 'var(--warm-brown)',
    cursor: 'pointer',
    fontSize: 22,
    lineHeight: 1,
    WebkitTapHighlightColor: 'transparent',
    borderRadius: 999,
  },
  ctaRow: {
    display: 'flex',
    gap: 8,
  },
  ctaPrimary: {
    flex: 1,
    minHeight: 40,
    background: 'var(--push, #E5736B)',
    color: '#fff',
    border: 'none',
    borderRadius: 999,
    fontFamily: 'var(--font-ui)',
    fontSize: 13,
    fontWeight: 700,
    letterSpacing: '0.04em',
    cursor: 'pointer',
    WebkitTapHighlightColor: 'transparent',
  },
};

export default function EndOfCycleLetter({ onSeeCalendar }) {
  const { user } = useApp();
  const t = useStrings();
  const [diaryRows, setDiaryRows] = useState(null);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    if (!user?.id || !supabase) { setDiaryRows([]); return; }
    let cancelled = false;
    const since = new Date();
    since.setDate(since.getDate() - 120);
    const sinceIso = `${since.getFullYear()}-${String(since.getMonth() + 1).padStart(2, '0')}-${String(since.getDate()).padStart(2, '0')}`;
    supabase
      .from('diary_entries')
      .select('entry_date, signals')
      .eq('user_id', user.id)
      .gte('entry_date', sinceIso)
      .then(({ data }) => {
        if (!cancelled) setDiaryRows(Array.isArray(data) ? data : []);
      })
      .catch(() => { if (!cancelled) setDiaryRows([]); });
    return () => { cancelled = true; };
  }, [user]);

  const summary = useMemo(() => {
    if (!Array.isArray(diaryRows) || diaryRows.length === 0) return null;
    return endOfCycleSummary(diaryRows);
  }, [diaryRows]);

  // Skip when this cycle's letter has already been seen (per
  // localStorage sentinel).
  const seen = useMemo(() => getSeenSet(), []);
  const alreadySeen = summary && seen.has(summary.cycleEnd);

  if (!summary || alreadySeen || hidden) return null;

  function dismiss() {
    markSeen(summary.cycleEnd);
    setHidden(true);
  }

  // Build the body sentence. When floor_feel data is rich (multiple
  // markers across the cycle), produce the full pattern line; when
  // sparse (just the length is meaningful), keep it short.
  let bodyLine = t('end_of_cycle_just_length').replace('{n}', summary.lengthDays);
  const parts = [];
  if (summary.heaviestRange) {
    // Single day vs range. The string template embeds "day {range}",
    // and we substitute the noun + number together so a multi-day
    // run reads "days 1 to 3" rather than "day 1 to 3".
    const isRange = summary.heaviestRange.start !== summary.heaviestRange.end;
    const dayWord = isRange ? t('end_of_cycle_days_word') : t('end_of_cycle_day_word');
    const numberPart = isRange
      ? `${summary.heaviestRange.start} ${t('end_of_cycle_to_word')} ${summary.heaviestRange.end}`
      : `${summary.heaviestRange.start}`;
    parts.push(
      t('end_of_cycle_heaviest')
        .replace('day {range}', `${dayWord} ${numberPart}`)
        .replace('dia {range}', `${dayWord} ${numberPart}`),
    );
  }
  if (summary.strongestDay) {
    parts.push(t('end_of_cycle_strongest').replace('{day}', summary.strongestDay));
  }
  if (summary.quietestDay) {
    parts.push(t('end_of_cycle_quietest').replace('{day}', summary.quietestDay));
  }
  if (parts.length > 0) {
    bodyLine = t('end_of_cycle_with_feels')
      .replace('{n}', summary.lengthDays)
      .replace('{parts}', parts.join(' '));
  }

  return (
    <div style={styles.card} role="status" aria-live="polite">
      <button type="button" onClick={dismiss} style={styles.closeBtn} aria-label={t('end_of_cycle_close')}>
        ×
      </button>
      <div style={styles.eyebrow}>
        <span style={styles.eyebrowDot} aria-hidden="true" />
        {t('end_of_cycle_eyebrow')}
      </div>
      <h2 style={styles.title}>{t('end_of_cycle_title')}</h2>
      <p style={styles.body}>{bodyLine}</p>
      <p style={styles.bodyMuted}>{t('end_of_cycle_invite')}</p>
      <div style={styles.ctaRow}>
        <button
          type="button"
          onClick={() => { dismiss(); onSeeCalendar?.(); }}
          style={styles.ctaPrimary}
        >
          {t('end_of_cycle_see_calendar')}
        </button>
      </div>
    </div>
  );
}
